/**
 * 幕一 · 备料台 —— 编排前的唯一输入面(2026-08-06 编排页重排)。
 *
 * ═══ 治的是什么 ═══════════════════════════════════════════════════════════════
 * 重排前左栏是一条 17 块的垂直堆叠,文案框在中间,而**喂给导演台的选项排在
 * 「AI 智能编排」按钮下方**。真机实测(1440×900):按钮在 docTop=476,产品图 545、
 * 选人物 664、风格与格式 733、选拍法 1004 —— 约 520px 的导演台输入排在消费它们的
 * 按钮之后,「选拍法」整块落在首屏外。用户顺着页面读到按钮就点了,引擎拿的是默认值。
 * **这不是审美问题,是输入静默丢失。**
 *
 * 所以顺序改成 ①目的 → ②格式 → ③文案+素材 → ④编排,
 * **视觉顺序 = 因果顺序**:读到那颗按钮时,喂给它的东西已经全部经过眼睛。
 *
 * ═══ ★为什么幕一没有右栏 cockpit ═══════════════════════════════════════════════
 * 真机量过:编排前右栏内容只到 690px 而页面高 1286px(**空 596px**);它此刻显示的
 * 比例 / 风格 / 时长 / 清晰度全是备料台自己控件的复读,预览框还写着「AI 编排后显示」。
 * 更糟的是它那颗「生成视频」与这里的「AI 智能编排」**同屏、同为亮色实心**在竞争,
 * 产品因此不得不加一个「裸发护栏」弹窗去拦点错的人 —— 用弹窗补一个由布局造成的错误。
 * 去掉幕一的右栏,三件事一起消失。积分明细挪到编排按钮旁边:花钱的正是这颗按钮。
 *
 * ═══ 一个容器一个强调色 ═══════════════════════════════════════════════════════
 * 重排前 `border-primary/30 bg-primary/5` 被四个块共用(LLM 概览 / 风格与格式 /
 * 海报意图 / 故事大纲)—— primary 于是不再表示"重要",只表示"一个块"。
 * 现在整页只有备料台带 primary 边框:它是这一幕真正要你看的东西。
 */
import { type Dispatch, type ReactNode, type SetStateAction } from "react";

import type { CharacterPick } from "@/entities/character/ui/CharacterPickerDialog";
import type { FormState } from "../../model/videoCreateTypes";
import { intakeMaterialHint } from "../../model/briefGuidance";
import { VideoFormatRow } from "./VideoFormatRow";
import { VideoMaterialRow } from "./VideoMaterialRow";
import { VideoPurposeRow } from "./VideoPurposeRow";

interface Props {
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  /** 编排/出图进行中:整台只读(brief 已被服务端快照,改了也不生效)。 */
  disabled?: boolean;
  /** 目的写入口(storyMode 映射在页面层 applyPurpose)。 */
  onPurposeChange: (label: string | null) => void;
  onPickCharacter: (pick: CharacterPick) => void;
  onClearCharacter: () => void;
  /**
   * ①②是否显示(BPWB-1 拆成两开关)。模版流已由模版预设目的/风格/时长(用户只填空),
   * 再给一套选项就是让他改一个已经替他定好的东西 —— 那正是模版的反面。
   * 蓝图态两行命运不同:目的/拍法被蓝图公式盖掉(一律藏),格式行只在仿拍档藏
   * (时长由原片定)—— 所以必须能各开各关,判定在 videoSource.showPurpose/showFormat。
   */
  showPurpose?: boolean;
  showFormat?: boolean;
  /** A4:画面层里是否就地渲时长(见上 showDurationInFormat 说明,透传给 VideoFormatRow)。 */
  showDurationInFormat?: boolean;
  /** F 批:画面层里是否就地渲比例。比例挪到时长那一行的流(目的流)传 false,透传给 VideoFormatRow。 */
  showRatioInFormat?: boolean;
  /** A5:机器层插槽(排在画面层与行动区之间);幕二不传。 */
  machineSlot?: ReactNode;
  /** 素材行是否显示(海报流已有参考图、模版流走填空,都不需要再传料)。 */
  showMaterial?: boolean;
  /**
   * 内容区最前的插槽(2026-09-12 B3):蓝图态把「① 换成你的产品」压进本卡的顶端,
   * 让「换产品(素材)+ 介绍产品(文案)」成为同一张备料卡(仿照自由流形制)。
   * 缺省不渲染 —— 自由 / 模版 / 海报流没有它。
   */
  leadingSlot?: ReactNode;
  /** ③ 内容区:自由文案框 / 模版填空 / 海报意图 —— 五来源各自的形态。 */
  children: ReactNode;
  /** ④ 行动区:编排按钮 + 积分 + 高级入口。 */
  action?: ReactNode;
  /** 文案字数(贴素材行尾)。 */
  counter?: string;
  /** SEEDANCE 真人出镜 P0(0914 D2):`?cast=real` 落地态,透传给素材行的人物面板。 */
  castReal?: boolean;
  onNavigateToRealCreate?: () => void;
  /** 1005:当前视频草稿 id,透传给素材行(识别图片账单挂草稿)。 */
  draftId?: number | null;
  /** 1013 V6:「从产品库开始」进页 → 素材行产品库抽屉自动弹出。 */
  autoOpenProductLibrary?: boolean;
  /** V6:抽屉已自动弹出(页面据此收掉一次性标记)。 */
  onProductLibraryAutoOpened?: () => void;
}

export function VideoIntakeDeck({
  form,
  setForm,
  disabled,
  onPurposeChange,
  onPickCharacter,
  onClearCharacter,
  showPurpose = true,
  showFormat = true,
  showDurationInFormat = true,
  showRatioInFormat = true,
  machineSlot,
  showMaterial = true,
  leadingSlot,
  children,
  action,
  counter,
  castReal,
  onNavigateToRealCreate,
  draftId,
  autoOpenProductLibrary,
  onProductLibraryAutoOpened,
}: Props) {
  /**
   * ★三层金字塔重排(2026-09-08 老板「视频创建页参数杂乱、主次不分」;2026-09-13 A4/A5 再排):
   *   层1「拍什么」(目的 → 文案 → 素材,连续不被打断)→ 层2「画面层」(比例 + 风格,常显,
   *   取消了「出成什么样」折叠)→ 层2.5「机器层」(machineSlot,模型/分辨率/…)→ 层3「行动」。
   *   ★时长档已随 A1 移进「时长×拍法 联动卡」(自由外壳选定目的时);无联动卡的流仍在画面层就地渲
   *     (showDurationInFormat)。
   */
  // A4:素材行按目的高亮的副文案(锁定后备料台按目的定制的红利;非产品/人物档返 null 不渲)。
  const materialHint = intakeMaterialHint(form.briefPurpose);
  return (
    <div
      data-video-intake-deck
      className="space-y-0 rounded-[calc(var(--radius)*1.15)] border-[1.5px] border-primary/40 bg-card/60 px-5 py-4"
    >
      {/* ── 层1 · 拍什么(目的锁定后只在「未选目的」的自由流出现;选定即由横幅接管) ── */}
      {showPurpose && (
        <div className="border-b border-border/60 pb-3.5">
          <VideoPurposeRow
            value={form.briefPurpose}
            onChange={onPurposeChange}
            disabled={disabled}
          />
        </div>
      )}

      <div className="py-3.5">
        {/* B3:蓝图态「① 换成你的产品」压进本卡顶端,与下面的文案框同属一张备料卡。 */}
        {leadingSlot}
        {children}
        {showMaterial && (
          <div className="mt-2.5">
            {/* A4:素材行按目的高亮 —— 带货/硬广/剧情建议传产品图,招聘/公司引到人物库。 */}
            {materialHint && (
              <p
                data-testid="material-purpose-hint"
                className="mb-1.5 text-2xs leading-snug text-muted-foreground"
              >
                {materialHint}
              </p>
            )}
            <VideoMaterialRow
              form={form}
              setForm={setForm}
              onPickCharacter={onPickCharacter}
              onClearCharacter={onClearCharacter}
              disabled={disabled}
              counter={counter}
              castReal={castReal}
              onNavigateToRealCreate={onNavigateToRealCreate}
              draftId={draftId}
              autoOpenProductLibrary={autoOpenProductLibrary}
              onProductLibraryAutoOpened={onProductLibraryAutoOpened}
            />
          </div>
        )}
      </div>

      {/* ── 层2 · 画面层(A4:取消「出成什么样」折叠,比例 + 风格常显)── */}
      {showFormat && (
        <div data-testid="video-picture-layer" className="border-t border-border/60 py-3.5">
          <span className="mb-2 block text-[13.5px] font-semibold text-foreground">画面层</span>
          <VideoFormatRow
            form={form}
            setForm={setForm}
            disabled={disabled}
            showDuration={showDurationInFormat}
            showRatio={showRatioInFormat}
          />
        </div>
      )}

      {/* ── 层2.5 · 机器层(A5:模型/分辨率/同步音频/任务名;排在画面层与行动区之间)── */}
      {machineSlot && (
        <div data-testid="video-machine-slot" className="border-t border-border/60 py-3.5">
          {machineSlot}
        </div>
      )}

      {/**
       * 配音与字幕:幕一原本完全不提,用户不知有默认;这里只作只读提示,真调在幕二逐镜面板。
       *
       * ★2026-09-12 四刀 ④ 合一:行动区下方原本还有一条 `video-voice-subtitle-where-hint`
       *   (2026-08-14 TOP#8 为「没有字幕功能 / 无法调语速」四人反馈写的),自由流里两条
       *   同屏出现、语义重叠。现在合成这一条 —— 两层意思一条不少:三样在幕二哪儿调,
       *   以及**不编排直发就没有配音字幕**(爱民 0812 同根:规则确定,只是界面从没说过)。
       *   testid 一并搬来,真栈用例照旧钉这一处。
       */}
      <div
        data-testid="video-voice-subtitle-where-hint"
        className="border-t border-border/60 py-2.5 text-2xs text-muted-foreground"
      >
        {/* 写成字符串字面量:裸 JSX 文本会被 prettier 按 100 列折行,渲染时中间凭空多个空格 */}
        {
          "配音、语速、字幕已有默认,编排完成后在分镜页「声音与字幕」逐镜微调;不编排直发则只出画面、无配音字幕。"
        }
      </div>

      {/* ── 层3 · 行动 ── */}
      {action && <div className="border-t border-border/60 pt-3.5">{action}</div>}
    </div>
  );
}
